import { collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, orderBy, query, where, limit, documentId, DocumentData, QuerySnapshot, DocumentSnapshot, Timestamp, serverTimestamp, writeBatch, startAfter, QueryConstraint } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, functions, storage } from './firebase';
import { getFunctions, httpsCallable } from '@firebase/functions';
import {
  Driver,
  DriverAssignmentMode,
  DriverHistorySummary,
  DriverShift,
  Order,
  Rating,
  OrderHistoryFilters,
} from '../models/Driver';
import { adminScopeService } from './adminScopeService';

export interface PaginatedDriverOrderHistoryResult {
  orders: Order[];
  lastDocument: DocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

export interface PaginatedDriverPaidShiftsResult {
  shifts: DriverShift[];
  lastDocument: DocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

export interface DeletePendingDriverRequestResult {
  success: boolean;
  driverDeleted: boolean;
  authDeleted: boolean;
  clientAccountPreserved: boolean;
  userDocExists: boolean;
}

class DriverService {
  private driversCollection = collection(db, 'drivers');
  private driverLocationsCollection = collection(db, 'driver_locations');
  private storesCollection = collection(db, 'stores');
  private ordersCollection = collection(db, 'orders');
  private ratingsCollection = collection(db, 'ratings');
  private driverShiftsCollection = collection(db, 'driver_shifts');

  private _buildOrderHistoryConstraints(filters?: OrderHistoryFilters): QueryConstraint[] {
    const queryConstraints: QueryConstraint[] = [];

    if (filters?.dateRange) {
      const startTimestamp = Timestamp.fromDate(filters.dateRange.startDate);
      const endDateInclusive = new Date(filters.dateRange.endDate);
      endDateInclusive.setHours(23, 59, 59, 999);
      const endTimestamp = Timestamp.fromDate(endDateInclusive);
      queryConstraints.push(where('createdAt', '>=', startTimestamp));
      queryConstraints.push(where('createdAt', '<=', endTimestamp));
    } else if (
      filters?.period &&
      (filters.period === 'day' || filters.period === 'week' || filters.period === 'month')
    ) {
      const now = new Date();
      let startDate = new Date();
      const endDate = new Date(now);

      switch (filters.period) {
        case 'day':
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(23, 59, 59, 999);
          break;
        case 'week':
          startDate.setDate(now.getDate() - now.getDay());
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(23, 59, 59, 999);
          break;
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(23, 59, 59, 999);
          break;
      }

      queryConstraints.push(where('createdAt', '>=', Timestamp.fromDate(startDate)));
      queryConstraints.push(where('createdAt', '<=', Timestamp.fromDate(endDate)));
    }

    return queryConstraints;
  }

  private _normalizeDriverPourcentage(rawValue: unknown, fallback = 100): number {
    if (rawValue === null || rawValue === undefined) {
      return fallback;
    }

    const parsed = Number(rawValue);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private _normalizeOptionalString(rawValue: unknown): string | null {
    if (typeof rawValue !== 'string') {
      return null;
    }

    const trimmed = rawValue.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private _normalizeAssignmentMode(rawValue: unknown): DriverAssignmentMode {
    return rawValue === 'store_locked' ? 'store_locked' : 'group_shared';
  }

  private _normalizeStringArray(rawValue: unknown): string[] {
    if (!Array.isArray(rawValue)) {
      return [];
    }

    return Array.from(
      new Set(
        rawValue
          .map((value) => this._normalizeOptionalString(value))
          .filter((value): value is string => value !== null)
      )
    ).sort();
  }

  private async _loadGroupStoreIds(groupId: string | null | undefined): Promise<string[]> {
    const normalizedGroupId = this._normalizeOptionalString(groupId);
    if (!normalizedGroupId) {
      return [];
    }

    const groupSnap = await getDoc(doc(db, 'operationGroups', normalizedGroupId));
    return this._normalizeStringArray(groupSnap.data()?.storeIds);
  }

  private async _resolveDeliveryStoreIds(options: {
    groupId?: string | null;
    assignedStoreId?: string | null;
    assignmentMode?: DriverAssignmentMode | null;
  }): Promise<string[]> {
    const assignmentMode = this._normalizeAssignmentMode(options.assignmentMode);
    const assignedStoreId = this._normalizeOptionalString(options.assignedStoreId);

    if (assignmentMode === 'store_locked') {
      return assignedStoreId ? [assignedStoreId] : [];
    }

    const groupStoreIds = await this._loadGroupStoreIds(options.groupId);
    if (groupStoreIds.length > 0) {
      return groupStoreIds;
    }

    return assignedStoreId ? [assignedStoreId] : [];
  }

  // Helper to convert Firestore DocumentData to Driver interface
  private _normalizeLocation(rawValue: unknown): { latitude: number; longitude: number } | undefined {
    if (!rawValue || typeof rawValue !== 'object') {
      return undefined;
    }

    const candidate = rawValue as { latitude?: unknown; longitude?: unknown };
    return typeof candidate.latitude === 'number' && typeof candidate.longitude === 'number'
      ? { latitude: candidate.latitude, longitude: candidate.longitude }
      : undefined;
  }

  private _locationFromData(data?: DocumentData | null): { latitude: number; longitude: number } | undefined {
    return this._normalizeLocation(data?.currentLocation) ?? this._normalizeLocation(data?.location);
  }

  private async _loadDriverLocationData(driverIds: string[]): Promise<Map<string, DocumentData>> {
    const ids = Array.from(new Set(driverIds.map((id) => id.trim()).filter(Boolean)));
    if (ids.length === 0) {
      return new Map();
    }

    const result = new Map<string, DocumentData>();
    for (let index = 0; index < ids.length; index += 10) {
      const chunk = ids.slice(index, index + 10);
      const snapshot = await getDocs(
        query(this.driverLocationsCollection, where(documentId(), 'in', chunk))
      );
      snapshot.docs.forEach((locationDoc) => {
        result.set(locationDoc.id, locationDoc.data());
      });
    }
    return result;
  }

  private _mapDocumentToDriver(doc: DocumentSnapshot<DocumentData>, locationData?: DocumentData | null): Driver {
    const data = doc.data() as any;
    const rawDriverPourcentage = data?.driverPourcentage ?? data?.driverPercentage;
    const rawRating = data?.rating;
    const normalizedRatingCandidate =
      typeof rawRating === 'number'
        ? rawRating
        : typeof data?.average_rating === 'number'
          ? data.average_rating
          : typeof rawRating?.average === 'number'
            ? rawRating.average
            : 0.0;
    const normalizedReviewCountCandidate =
      typeof data?.review_count === 'number'
        ? data.review_count
        : typeof data?.total_ratings_count === 'number'
          ? data.total_ratings_count
          : typeof rawRating?.count === 'number'
            ? rawRating.count
            : 0;
    const normalizedRating = Number.isFinite(normalizedRatingCandidate)
      ? normalizedRatingCandidate
      : 0.0;
    const normalizedReviewCount = Number.isFinite(normalizedReviewCountCandidate)
      ? normalizedReviewCountCandidate
      : 0;
    return {
      id: doc.id,
      groupId: this._normalizeOptionalString(data?.groupId) ?? undefined,
      assignedStoreId: this._normalizeOptionalString(data?.assignedStoreId),
      assignmentMode: this._normalizeAssignmentMode(data?.assignmentMode),
      deliveryStoreIds: this._normalizeStringArray(data?.deliveryStoreIds),
      approvalStatus: typeof data?.approvalStatus === 'string' ? data.approvalStatus : undefined,
      firstName: data?.firstName ?? '',
      lastName: data?.lastName ?? '',
      phone: data?.phone ?? '',
      address: data?.address,
      vehicleType: data?.vehicleType,
      vehicleRegistration: data?.vehicleRegistration,
      availability: data?.availability ?? 'offline',
      enabled: data?.enabled === true,
      email: data?.email,
      cin: data?.cin,
      activityStatus: data?.activityStatus ?? 'free',
      currentOrderId: data?.currentOrderId ?? null,
      currentOrderIds: Array.isArray(data?.currentOrderIds) ? data.currentOrderIds : [],
      currentUserIds: Array.isArray(data?.currentUserIds) ? data.currentUserIds : [],
      photoUrl: data?.photoUrl,
      driverPourcentage: this._normalizeDriverPourcentage(rawDriverPourcentage),

      rating: normalizedRating,
      review_count: normalizedReviewCount,
      is_favorite: data?.is_favorite ?? false,
      location: this._locationFromData(locationData) ?? this._locationFromData(data),

      orders_accepted_count: typeof data?.orders_accepted_count === 'number' ? data.orders_accepted_count : (data?.orders_accepted_count ? Number(data.orders_accepted_count) : 0),
      orders_ignored_count: typeof data?.orders_ignored_count === 'number' ? data.orders_ignored_count : (data?.orders_ignored_count ? Number(data.orders_ignored_count) : 0),
      orders_refused_count: typeof data?.orders_refused_count === 'number' ? data.orders_refused_count : (data?.orders_refused_count ? Number(data.orders_refused_count) : 0),

      // New suspension/ban fields
      isSuspended: data?.isSuspended,
      suspensionReason: data?.suspensionReason,
      suspendedAt: data?.suspendedAt,
      suspendedBy: data?.suspendedBy,
      unbannedAt: data?.unbannedAt,
      unbannedBy: data?.unbannedBy,
      createdAt: data?.createdAt instanceof Timestamp ? data.createdAt : undefined,
      updatedAt: data?.updatedAt instanceof Timestamp ? data.updatedAt : undefined,
    };
  }

  // Helper to convert Firestore DocumentData to Order interface
  private _mapDocumentToOrder(doc: DocumentSnapshot<DocumentData>): Order {
    const data = doc.data() as any;
    const realDeliveryPrice = typeof data?.driverPayout === 'number'
      ? data.driverPayout
      : Number(data?.driverPayout ?? 0);
    const driverCommissionAmount =
      data?.driverCommissionAmount === null || data?.driverCommissionAmount === undefined
        ? undefined
        : Number(data.driverCommissionAmount);
    const driverCommissionPercentageSnapshot =
      data?.driverCommissionPercentageSnapshot === null ||
      data?.driverCommissionPercentageSnapshot === undefined
        ? undefined
        : Number(data.driverCommissionPercentageSnapshot);
    const rawPaymentMethod = data?.paymentMethod ?? 'cash';
    const paymentMethod =
      typeof rawPaymentMethod === 'string' && rawPaymentMethod.toLowerCase() === 'cash'
        ? 'cash'
        : 'card';

    return {
      id: doc.id,
      createdAt: data?.createdAt instanceof Timestamp ? data.createdAt : Timestamp.now(),
      status: data?.status ?? 'UNKNOWN',
      totalAmount: data?.totalAmount ?? 0.0,
      realDeliveryPrice: realDeliveryPrice,
      driverPayoutAmount: driverCommissionAmount,
      paymentMethod,
      driverCommissionAmount,
      driverCommissionPercentageSnapshot,
    };
  }

  // Helper to convert Firestore DocumentData to Rating interface
  private _mapDocumentToRating(doc: DocumentSnapshot<DocumentData>): Rating {
    const data = doc.data() as any;
    return {
      id: doc.id,
      driver_id: data?.driver_id ?? '',
      order_id: data?.order_id, // Peut être null
      rating: data?.rating ?? 0.0,
      review_text: data?.review_text,
      created_at: data?.created_at instanceof Timestamp ? data.created_at : Timestamp.now(),
    };
  }

  private _mapDocumentToDriverShift(doc: DocumentSnapshot<DocumentData>): DriverShift {
    const data = doc.data() as any;
    return {
      id: doc.id,
      driverId: data?.driverId ?? '',
      driverName: data?.driverName ?? '',
      driverPhone: data?.driverPhone ?? '',
      status: data?.status ?? 'open',
      startedAt: data?.startedAt instanceof Timestamp ? data.startedAt : undefined,
      paidClosedAt: data?.paidClosedAt instanceof Timestamp ? data.paidClosedAt : undefined,
      totalOrders: typeof data?.totalOrders === 'number' ? data.totalOrders : Number(data?.totalOrders ?? 0),
      totalDeliveryBase: typeof data?.totalDeliveryBase === 'number' ? data.totalDeliveryBase : Number(data?.totalDeliveryBase ?? 0),
      totalCommission: typeof data?.totalCommission === 'number' ? data.totalCommission : Number(data?.totalCommission ?? 0),
      totalCashCollected: typeof data?.totalCashCollected === 'number' ? data.totalCashCollected : Number(data?.totalCashCollected ?? 0),
      driverCommissionPercentageSnapshot: typeof data?.driverCommissionPercentageSnapshot === 'number'
        ? data.driverCommissionPercentageSnapshot
        : Number(data?.driverCommissionPercentageSnapshot ?? 0),
      settlementProcessing: data?.settlementProcessing === true,
    };
  }

  // Get all drivers
  async getDrivers(): Promise<Driver[]> {
    try {
      const currentGroupId = adminScopeService.getCurrentGroupId();
      const q = currentGroupId
        ? query(this.driversCollection, where('groupId', '==', currentGroupId))
        : query(this.driversCollection, orderBy('firstName'));
      const snapshot: QuerySnapshot<DocumentData> = await getDocs(q);
      const locationData = await this._loadDriverLocationData(snapshot.docs.map((doc) => doc.id));
      let drivers = snapshot.docs.map(doc => this._mapDocumentToDriver(doc, locationData.get(doc.id)));
      if (currentGroupId) {
        drivers = drivers.filter((driver) => driver.groupId === currentGroupId);
      }
      return drivers.sort((left, right) =>
        `${left.firstName} ${left.lastName}`.localeCompare(`${right.firstName} ${right.lastName}`)
      );
    } catch (error) {
      console.error('Error fetching drivers:', error);
      throw error;
    }
  }

  async getPendingDriverRequests(): Promise<Driver[]> {
    try {
      const snapshot: QuerySnapshot<DocumentData> = await getDocs(
        query(this.driversCollection, orderBy('firstName'))
      );
      const locationData = await this._loadDriverLocationData(snapshot.docs.map((doc) => doc.id));

      return snapshot.docs
        .map((doc) => this._mapDocumentToDriver(doc, locationData.get(doc.id)))
        .filter((driver) => !driver.groupId || driver.groupId.trim().length === 0)
        .sort((left, right) => {
          const rightTs = right.createdAt?.toMillis?.() ?? 0;
          const leftTs = left.createdAt?.toMillis?.() ?? 0;
          return rightTs - leftTs;
        });
    } catch (error) {
      console.error('Error fetching pending driver requests:', error);
      throw error;
    }
  }

  // Get a single driver by ID
  async getDriverById(driverId: string): Promise<Driver | null> {
    try {
      const docRef = doc(this.driversCollection, driverId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const locationSnap = await getDoc(doc(this.driverLocationsCollection, driverId));
        return this._mapDocumentToDriver(docSnap, locationSnap.data());
      }
      return null;
    } catch (error) {
      console.error(`Error fetching driver ${driverId}:`, error);
      throw error;
    }
  }

  // Add a new driver
  async addDriver(driverData: Omit<Driver, 'id' | 'rating' | 'review_count' | 'orders_accepted_count' | 'orders_ignored_count' | 'orders_refused_count' | 'location'>, photoFile?: File | null): Promise<string> {
    try {
      const currentGroupId = adminScopeService.getCurrentGroupId();
      const currentStoreId = adminScopeService.getCurrentStoreId();
      const { driverPercentage: _legacyDriverPercentage, ...restDriverData } = driverData as any;
      const groupId = this._normalizeOptionalString(restDriverData.groupId) ?? currentGroupId ?? undefined;
      const assignedStoreId =
        this._normalizeOptionalString(restDriverData.assignedStoreId) ??
        this._normalizeOptionalString(currentStoreId);
      const assignmentMode = this._normalizeAssignmentMode(restDriverData.assignmentMode);
      const deliveryStoreIds = await this._resolveDeliveryStoreIds({
        groupId,
        assignedStoreId,
        assignmentMode,
      });
      const dataToCreate = {
        ...restDriverData,
        groupId,
        assignedStoreId,
        assignmentMode,
        deliveryStoreIds,
        driverPourcentage: this._normalizeDriverPourcentage(restDriverData.driverPourcentage),
        rating: 0.0,
        review_count: 0,
        is_favorite: false,
        orders_accepted_count: 0,
        orders_ignored_count: 0,
        orders_refused_count: 0,
      };

      const docRef = await addDoc(this.driversCollection, dataToCreate);
      const driverId = docRef.id;

      if (photoFile) {
        const photoUrl = await this.uploadDriverPhoto(driverId, photoFile);
        await updateDoc(docRef, { photoUrl: photoUrl });
      }

      return driverId;
    } catch (error) {
      console.error('Error adding driver:', error);
      throw error;
    }
  }

  // Update an existing driver
  async updateDriver(driverId: string, driverData: Partial<Omit<Driver, 'id' | 'rating' | 'review_count' | 'orders_accepted_count' | 'orders_ignored_count' | 'orders_refused_count' | 'location'>>, photoFile?: File | null): Promise<void> {
    try {
      const currentGroupId = adminScopeService.getCurrentGroupId();
      const updatePayload: any = { ...driverData };
      delete updatePayload.driverPercentage;
      const driverRef = doc(this.driversCollection, driverId);

      if (updatePayload.driverPourcentage !== undefined) {
        updatePayload.driverPourcentage = this._normalizeDriverPourcentage(
          updatePayload.driverPourcentage
        );
      }

      if (photoFile) {
        const photoUrl = await this.uploadDriverPhoto(driverId, photoFile);
        updatePayload.photoUrl = photoUrl;
      } else if (driverData.photoUrl === null) {
        updatePayload.photoUrl = null;
      }

      if (updatePayload.groupId === undefined && currentGroupId) {
        updatePayload.groupId = currentGroupId;
      }

      if (updatePayload.assignmentMode !== undefined) {
        updatePayload.assignmentMode = this._normalizeAssignmentMode(updatePayload.assignmentMode);
      }

      if (updatePayload.assignedStoreId !== undefined) {
        updatePayload.assignedStoreId = this._normalizeOptionalString(updatePayload.assignedStoreId);
      }

      const scopeChanged =
        updatePayload.groupId !== undefined ||
        updatePayload.assignedStoreId !== undefined ||
        updatePayload.assignmentMode !== undefined;

      if (scopeChanged) {
        const existingSnap = await getDoc(driverRef);
        const existingData = existingSnap.data() ?? {};
        const nextGroupId = this._normalizeOptionalString(
          updatePayload.groupId ?? existingData.groupId
        );
        const nextAssignedStoreId = this._normalizeOptionalString(
          updatePayload.assignedStoreId ?? existingData.assignedStoreId ?? existingData.storeId
        );
        const nextAssignmentMode = this._normalizeAssignmentMode(
          updatePayload.assignmentMode ?? existingData.assignmentMode
        );
        updatePayload.deliveryStoreIds = await this._resolveDeliveryStoreIds({
          groupId: nextGroupId,
          assignedStoreId: nextAssignedStoreId,
          assignmentMode: nextAssignmentMode,
        });
      }

      // Remove undefined values from updatePayload to avoid Firestore errors
      Object.keys(updatePayload).forEach(key => {
        if (updatePayload[key] === undefined) {
          delete updatePayload[key];
        }
      });

      await updateDoc(driverRef, updatePayload);
    } catch (error) {
      console.error(`Error updating driver ${driverId}:`, error);
      throw error;
    }
  }

  async assignDriverToStore(
    driverId: string,
    storeId: string,
    options?: { activateNow?: boolean; assignmentMode?: DriverAssignmentMode }
  ): Promise<void> {
    try {
      const storeRef = doc(this.storesCollection, storeId);
      const storeSnap = await getDoc(storeRef);

      if (!storeSnap.exists()) {
        throw new Error('Magasin introuvable pour cette affectation.');
      }

      const storeData = storeSnap.data() as { operationGroupId?: string | null; name?: string };
      const groupId =
        typeof storeData.operationGroupId === 'string' && storeData.operationGroupId.trim().length > 0
          ? storeData.operationGroupId.trim()
          : null;

      if (!groupId) {
        throw new Error('Le magasin sélectionné n’est rattaché à aucun groupe opérationnel.');
      }

      const payload: Record<string, any> = {
        groupId,
        assignedStoreId: storeId,
        assignmentMode: this._normalizeAssignmentMode(options?.assignmentMode),
        deliveryStoreIds: await this._resolveDeliveryStoreIds({
          groupId,
          assignedStoreId: storeId,
          assignmentMode: this._normalizeAssignmentMode(options?.assignmentMode),
        }),
        updatedAt: serverTimestamp(),
      };

      if (options?.activateNow) {
        payload.enabled = true;
        payload.approvalStatus = 'approved';
      }

      await updateDoc(doc(this.driversCollection, driverId), payload);
    } catch (error) {
      console.error(`Error assigning driver ${driverId} to store ${storeId}:`, error);
      throw error;
    }
  }

  // Delete a driver
  async deleteDriver(driverId: string): Promise<void> {
    try {
      const driverRef = doc(this.driversCollection, driverId);
      await deleteDoc(driverRef);
    } catch (error) {
      console.error(`Error deleting driver ${driverId}:`, error);
      throw error;
    }
  }

  async deletePendingDriverRequest(driverId: string): Promise<DeletePendingDriverRequestResult> {
    try {
      const callable = httpsCallable<
        { driverId: string },
        DeletePendingDriverRequestResult
      >(functions, 'deletePendingDriverRequest');
      const result = await callable({ driverId });
      return result.data;
    } catch (error) {
      console.error(`Error deleting pending driver request ${driverId}:`, error);
      throw error;
    }
  }

  // Update the 'enabled' status of a driver
  async updateEnabledStatus(driverId: string, enabled: boolean): Promise<void> {
    try {
      const driverRef = doc(this.driversCollection, driverId);
      const driverSnap = await getDoc(driverRef);
      if (driverSnap.exists() && driverSnap.data()?.isSuspended === true) {
        throw new Error('Le livreur est banni. Utilisez "Débannir" pour réactiver son compte.');
      }
      const payload: Record<string, any> = {
        enabled,
        updatedAt: serverTimestamp(),
      };
      if (enabled) {
        payload.approvalStatus = 'approved';
      }
      await updateDoc(driverRef, payload);
    } catch (error) {
      console.error(`Error updating enabled status for driver ${driverId}:`, error);
      throw error;
    }
  }

  // Update the 'is_favorite' status of a driver
  async updateFavoriteStatus(driverId: string, isFavorite: boolean): Promise<void> {
    try {
      const driverRef = doc(this.driversCollection, driverId);
      await updateDoc(driverRef, { is_favorite: isFavorite });
    } catch (error) {
      console.error(`Error updating favorite status for driver ${driverId}:`, error);
      throw error;
    }
  }

  // Update the 'availability' status of a driver (online/offline)
  async updateAvailability(driverId: string, availability: 'online' | 'offline'): Promise<void> {
    try {
      const driverRef = doc(this.driversCollection, driverId);
      await updateDoc(driverRef, { availability });
    } catch (error) {
      console.error(`Error updating availability for driver ${driverId}:`, error);
      throw error;
    }
  }

  // Upload a driver photo to Firebase Storage
  async uploadDriverPhoto(driverId: string, photoFile: File): Promise<string> {
    try {
      // Utilisez le même chemin que pour les photos existantes: livreur_photos
      // Et utilisez l'ID du livreur comme nom de fichier pour plus de consistance
      const fileExtension = photoFile.name.split('.').pop() || 'jpg';
      const storageRef = ref(storage, `livreur_photos/${driverId}.${fileExtension}`);
      const snapshot = await uploadBytes(storageRef, photoFile);
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log(`Uploaded photo for driver ${driverId}. URL: ${downloadURL}`);
      return downloadURL;
    } catch (error) {
      console.error(`Error uploading photo for driver ${driverId}:`, error);
      throw error;
    }
  }

  // Fetch order history for a specific driver with filters
  async getDriverOrderHistory(driverId: string, filters?: OrderHistoryFilters): Promise<Order[]> {
    try {
      let queryConstraints: any[] = [
        where('deliveryPerson.id', '==', driverId),
      ];
      
      // Status filtering disabled - only date/period filtering is supported
      
      // Apply date range filter if provided
      if (filters?.dateRange) {
        const startTimestamp = Timestamp.fromDate(filters.dateRange.startDate);
        // Set end date to the end of the day (23:59:59.999)
        const endDateInclusive = new Date(filters.dateRange.endDate);
        endDateInclusive.setHours(23, 59, 59, 999);
        const endTimestamp = Timestamp.fromDate(endDateInclusive);
        queryConstraints.push(where('createdAt', '>=', startTimestamp));
        queryConstraints.push(where('createdAt', '<=', endTimestamp));
      }
      // Apply period filter for day/week/month only
      else if (filters?.period && (filters.period === 'day' || filters.period === 'week' || filters.period === 'month')) {
        const now = new Date();
        let startDate = new Date();
        let endDate = new Date(now); // Start with current date for end date calculation
        
        switch (filters.period) {
          case 'day':
            startDate.setHours(0, 0, 0, 0); // Start of today
            endDate.setHours(23, 59, 59, 999); // End of today
            break;
          case 'week':
            startDate.setDate(now.getDate() - now.getDay()); // Start of current week (Sunday)
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999); // End of current day
            break;
          case 'month':
            startDate.setDate(1); // Start of current month
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999); // End of current day
            break;
        }
        
        const startTimestamp = Timestamp.fromDate(startDate);
        const endTimestamp = Timestamp.fromDate(endDate);
        queryConstraints.push(where('createdAt', '>=', startTimestamp));
        queryConstraints.push(where('createdAt', '<=', endTimestamp));
      }
      
      queryConstraints.push(orderBy('createdAt', 'desc'));
      
      const q = query(this.ordersCollection, ...queryConstraints);
      const snapshot: QuerySnapshot<DocumentData> = await getDocs(q);
      
      return snapshot.docs.map(doc => this._mapDocumentToOrder(doc));
    } catch (error) {
      console.error(`Error fetching order history for driver ${driverId}:`, error);
      throw error;
    }
  }

  async getDriverOrderHistoryPage(
    driverId: string,
    filters?: OrderHistoryFilters,
    options?: {
      pageSize?: number;
      startAfterDocument?: DocumentSnapshot<DocumentData> | null;
    }
  ): Promise<PaginatedDriverOrderHistoryResult> {
    try {
      const pageSize = options?.pageSize ?? 25;
      const queryConstraints: QueryConstraint[] = [
        where('deliveryPerson.id', '==', driverId),
        ...this._buildOrderHistoryConstraints(filters),
        orderBy('createdAt', 'desc'),
        limit(pageSize),
      ];

      if (options?.startAfterDocument) {
        queryConstraints.push(startAfter(options.startAfterDocument));
      }

      const q = query(this.ordersCollection, ...queryConstraints);
      const snapshot: QuerySnapshot<DocumentData> = await getDocs(q);

      return {
        orders: snapshot.docs.map((doc) => this._mapDocumentToOrder(doc)),
        lastDocument: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null,
        hasMore: snapshot.docs.length === pageSize,
      };
    } catch (error) {
      console.error(`Error fetching paginated order history for driver ${driverId}:`, error);
      throw error;
    }
  }

  async getDriverPaidShiftsPage(
    driverId: string,
    filters?: OrderHistoryFilters,
    options?: {
      pageSize?: number;
      startAfterDocument?: DocumentSnapshot<DocumentData> | null;
    }
  ): Promise<PaginatedDriverPaidShiftsResult> {
    try {
      const pageSize = options?.pageSize ?? 10;
      const queryConstraints: QueryConstraint[] = [
        where('driverId', '==', driverId),
        where('status', '==', 'paid_closed'),
      ];

      if (filters?.dateRange) {
        const startTimestamp = Timestamp.fromDate(filters.dateRange.startDate);
        const endDateInclusive = new Date(filters.dateRange.endDate);
        endDateInclusive.setHours(23, 59, 59, 999);
        const endTimestamp = Timestamp.fromDate(endDateInclusive);
        queryConstraints.push(where('paidClosedAt', '>=', startTimestamp));
        queryConstraints.push(where('paidClosedAt', '<=', endTimestamp));
      } else if (
        filters?.period &&
        (filters.period === 'day' || filters.period === 'week' || filters.period === 'month')
      ) {
        const now = new Date();
        let startDate = new Date();
        const endDate = new Date(now);

        switch (filters.period) {
          case 'day':
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999);
            break;
          case 'week':
            startDate.setDate(now.getDate() - now.getDay());
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999);
            break;
          case 'month':
            startDate = new Date(now.getFullYear(), now.getMonth(), 1);
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999);
            break;
        }

        queryConstraints.push(where('paidClosedAt', '>=', Timestamp.fromDate(startDate)));
        queryConstraints.push(where('paidClosedAt', '<=', Timestamp.fromDate(endDate)));
      }

      queryConstraints.push(orderBy('paidClosedAt', 'desc'));
      queryConstraints.push(limit(pageSize));

      if (options?.startAfterDocument) {
        queryConstraints.push(startAfter(options.startAfterDocument));
      }

      const q = query(this.driverShiftsCollection, ...queryConstraints);
      const snapshot: QuerySnapshot<DocumentData> = await getDocs(q);

      return {
        shifts: snapshot.docs.map((doc) => this._mapDocumentToDriverShift(doc)),
        lastDocument: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null,
        hasMore: snapshot.docs.length === pageSize,
      };
    } catch (error) {
      console.error(`Error fetching paginated paid shifts for driver ${driverId}:`, error);
      throw error;
    }
  }

  // Fetch ratings for a specific driver
  async getDriverRatings(driverId: string): Promise<Rating[]> {
    try {
      const q = query(
        this.ratingsCollection,
        where('driver_id', '==', driverId),
        orderBy('created_at', 'desc')
      );
      const snapshot: QuerySnapshot<DocumentData> = await getDocs(q);
      return snapshot.docs.map(doc => this._mapDocumentToRating(doc));
    } catch (error) {
      console.error(`Error fetching ratings for driver ${driverId}:`, error);
      throw error;
    }
  }

  async getRatingsForOrderIds(orderIds: string[]): Promise<Record<string, Rating>> {
    try {
      const normalizedOrderIds = orderIds.map((orderId) => orderId.trim()).filter(Boolean);
      if (normalizedOrderIds.length === 0) {
        return {};
      }

      const ratingsByOrderId: Record<string, Rating> = {};
      const chunkSize = 10;

      for (let index = 0; index < normalizedOrderIds.length; index += chunkSize) {
        const chunk = normalizedOrderIds.slice(index, index + chunkSize);
        const q = query(this.ratingsCollection, where('order_id', 'in', chunk));
        const snapshot: QuerySnapshot<DocumentData> = await getDocs(q);

        snapshot.docs.forEach((ratingDoc) => {
          const rating = this._mapDocumentToRating(ratingDoc);
          if (rating.order_id) {
            ratingsByOrderId[rating.order_id] = rating;
          }
        });
      }

      return ratingsByOrderId;
    } catch (error) {
      console.error('Error fetching ratings for order ids:', error);
      throw error;
    }
  }

  async getDriverHistorySummary(
    driverId: string,
    filters?: OrderHistoryFilters
  ): Promise<DriverHistorySummary> {
    const callable = httpsCallable<
      {
        driverId: string;
        period?: OrderHistoryFilters['period'] | 'all';
        startDate?: string;
        endDate?: string;
      },
      {
        success: boolean;
        ordersSummary: DriverHistorySummary['ordersSummary'];
        paidShiftsSummary: DriverHistorySummary['paidShiftsSummary'];
      }
    >(functions, 'getDriverHistorySummary');

    const result = await callable({
      driverId,
      period: filters?.dateRange ? 'custom' : (filters?.period ?? 'all'),
      startDate: filters?.dateRange?.startDate?.toISOString(),
      endDate: filters?.dateRange?.endDate?.toISOString(),
    });

    return {
      ordersSummary: result.data.ordersSummary,
      paidShiftsSummary: result.data.paidShiftsSummary,
    };
  }

  async getOpenDriverShift(driverId: string): Promise<DriverShift | null> {
    try {
      const q = query(
        this.driverShiftsCollection,
        where('driverId', '==', driverId),
        where('status', '==', 'open')
      );
      const snapshot: QuerySnapshot<DocumentData> = await getDocs(q);
      if (snapshot.empty) {
        return null;
      }

      const shifts = snapshot.docs
        .map((doc) => this._mapDocumentToDriverShift(doc))
        .sort((left, right) => {
          const leftMillis = left.startedAt?.toMillis() ?? 0;
          const rightMillis = right.startedAt?.toMillis() ?? 0;
          return rightMillis - leftMillis;
        });

      return shifts.find((shift) => !shift.settlementProcessing) ?? shifts[0];
    } catch (error) {
      console.error(`Error fetching open shift for driver ${driverId}:`, error);
      throw error;
    }
  }

  async getPaidDriverShifts(driverId: string): Promise<DriverShift[]> {
    try {
      const q = query(
        this.driverShiftsCollection,
        where('driverId', '==', driverId),
        where('status', '==', 'paid_closed')
      );
      const snapshot: QuerySnapshot<DocumentData> = await getDocs(q);
      return snapshot.docs
        .map((doc) => this._mapDocumentToDriverShift(doc))
        .sort((left, right) => {
          const leftMillis = left.paidClosedAt?.toMillis() ?? 0;
          const rightMillis = right.paidClosedAt?.toMillis() ?? 0;
          return rightMillis - leftMillis;
        });
    } catch (error) {
      console.error(`Error fetching paid shifts for driver ${driverId}:`, error);
      throw error;
    }
  }

  // Fetch ratings for a specific order
  async getOrderRating(orderId: string): Promise<Rating | null> {
    try {
      const q = query(
        this.ratingsCollection,
        where('order_id', '==', orderId),
        limit(1)
      );
      const snapshot: QuerySnapshot<DocumentData> = await getDocs(q);
      if (snapshot.empty) {
        return null;
      }
      return this._mapDocumentToRating(snapshot.docs[0]);
    } catch (error) {
      console.error(`Error fetching rating for order ${orderId}:`, error);
      throw error;
    }
  }

  // Ban a driver using Cloud Function
  async banDriver(driverId: string, reason?: string): Promise<boolean> {
    try {
      const functions = getFunctions();
      const banDriverFunction = httpsCallable(functions, 'banDriver');
      const finalReason = reason?.trim() || 'Bannissement effectué depuis le back-office.';
      
      await banDriverFunction({
        driverId: driverId,
        reason: finalReason,
      });
      
      console.log(`Driver ${driverId} banned successfully via Cloud Function.`);
      return true;
    } catch (error) {
      console.error(`Error banning driver ${driverId}:`, error);
      throw error;
    }
  }

  // Unban a driver using Cloud Function
  async unbanDriver(driverId: string, reason?: string): Promise<boolean> {
    try {
      const functions = getFunctions();
      const unbanDriverFunction = httpsCallable(functions, 'unbanDriver');

      await unbanDriverFunction({
        driverId: driverId,
        ...(reason && { reason })
      });

      console.log(`Driver ${driverId} unbanned successfully via Cloud Function.`);
      return true;
    } catch (error) {
      console.error(`Error unbanning driver ${driverId}:`, error);
      throw error;
    }
  }

  // Reset a specific driver counter (direct Firestore update)
  async resetDriverCounter(
    driverId: string,
    counterField: 'orders_accepted_count' | 'orders_ignored_count' | 'orders_refused_count'
  ): Promise<void> {
    try {
      const driverRef = doc(this.driversCollection, driverId);
      await updateDoc(driverRef, {
        [counterField]: 0,
        updatedAt: serverTimestamp(),
      } as any);
    } catch (error) {
      console.error(`Error resetting counter ${counterField} for driver ${driverId}:`, error);
      throw error;
    }
  }

  // Force-set activity status to free and clear current order fields
  async setActivityStatusFree(driverId: string): Promise<void> {
    try {
      const driverRef = doc(this.driversCollection, driverId);
      await updateDoc(driverRef, {
        activityStatus: 'free',
        currentOrderId: null,
        currentOrderIds: [],
        currentUserIds: [],
        updatedAt: serverTimestamp(),
      } as any);
    } catch (error) {
      console.error(`Error setting activity status to free for driver ${driverId}:`, error);
      throw error;
    }
  }

  // Apply a global percentage to all drivers (batch write)
  async applyGlobalPercentage(percentage: number): Promise<number> {
    try {
      const snapshot = await getDocs(this.driversCollection);
      const currentGroupId = adminScopeService.getCurrentGroupId();
      const scopedDocs = snapshot.docs.filter((driverDoc) => {
        if (!currentGroupId) {
          return true;
        }
        return driverDoc.data()?.groupId === currentGroupId;
      });
      const batchSize = 500; // Firestore batch limit
      let updatedCount = 0;

      for (let i = 0; i < scopedDocs.length; i += batchSize) {
        const batch = writeBatch(db);
        const chunk = scopedDocs.slice(i, i + batchSize);

        for (const driverDoc of chunk) {
          batch.update(driverDoc.ref, {
            driverPourcentage: percentage,
            updatedAt: serverTimestamp(),
          });
          updatedCount++;
        }

        await batch.commit();
      }

      return updatedCount;
    } catch (error) {
      console.error('Error applying global percentage:', error);
      throw error;
    }
  }
}

export const driverService = new DriverService();
